// Adapters between the API row (`LeadRow` from `@barel/api-client/react`)
// and the DS-D composite shapes (`LeadRowData`, `LeadDetailData`).
//
// We keep this module thin and side-effect free so it's straightforward to
// unit-test (no React deps).

import type { LeadRow as ApiLead } from '@barel/api-client/react';
import type { LeadDetailData, LeadRowData } from '@barel/ui';

import { formatDate } from '@/lib/format';

export interface AccountLite {
  id: string;
  name: string;
  color?: string | null;
}

export interface BusinessLite {
  id: string;
  name: string;
}

/**
 * Promote a `validatedFrom` string to the narrower `LeadValidationSource`
 * the DS composite types accept. Server can send `'extension'` (US-153) — we
 * map it to `'inbox'` for the existing DS composite which only knows about
 * the original three sources. The detail-panel banner surfaces the original
 * source separately when it diverges.
 */
export function toComposiSource(
  validatedFrom: ApiLead['validatedFrom'],
): 'inbox' | 'slack' | 'discord' {
  if (validatedFrom === 'slack' || validatedFrom === 'discord') return validatedFrom;
  return 'inbox';
}

export function toRowData(
  lead: ApiLead,
  account: AccountLite | undefined,
  business: BusinessLite | undefined,
  formattedLastActivity: string,
): LeadRowData {
  return {
    convId: lead.conversationId ?? lead.id,
    name: lead.prospectName,
    initials: lead.prospectInitials,
    title: lead.prospectTitle ?? '—',
    company: lead.prospectCompany ?? '—',
    accountId: account?.id ?? lead.linkedinAccountId ?? '',
    accountLabel: account?.name ?? '—',
    accountColor: account?.color ?? lead.prospectColor ?? '#5b6ef8',
    business: business?.name ?? '—',
    lastAction: lead.lastAction ?? '—',
    lastActivityAt: formattedLastActivity,
    validatedFrom: toComposiSource(lead.validatedFrom),
    source: lead.sourcePill,
    status: lead.status,
    crmSync: Boolean(lead.crmSync),
    crmTarget: lead.crmTarget ?? undefined,
  };
}

export function toDetailData(
  lead: ApiLead,
  account: AccountLite | undefined,
  business: BusinessLite | undefined,
  formattedLastActivity: string,
): LeadDetailData {
  return {
    convId: lead.conversationId ?? lead.id,
    name: lead.prospectName,
    initials: lead.prospectInitials,
    title: lead.prospectTitle ?? '—',
    company: lead.prospectCompany ?? '—',
    accountId: account?.id ?? lead.linkedinAccountId ?? '',
    accountLabel: account?.name ?? '—',
    accountColor: account?.color ?? lead.prospectColor ?? '#5b6ef8',
    business: business?.name ?? '—',
    lastAction: lead.lastAction ?? '—',
    lastActivityAt: formattedLastActivity,
    validatedFrom: toComposiSource(lead.validatedFrom),
    source: lead.sourcePill,
    status: lead.status,
    crmSync: Boolean(lead.crmSync),
    crmTarget: lead.crmTarget ?? undefined,
  };
}

/**
 * Format the last-activity timestamp the same way the proto does. We prefer
 * a relative day string for recent activity ("Aujourd'hui" / "Hier") and
 * fall back to a short locale date.
 */
export function formatLastActivity(
  raw: string | number | null | undefined,
  locale: string,
  todayLabel: string,
  yesterdayLabel: string,
  now: Date = new Date(),
): { iso: string; label: string } {
  if (!raw) return { iso: '', label: '—' };
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return { iso: '', label: '—' };
  const iso = date.toISOString();
  const startOfDay = (d: Date): number => {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x.getTime();
  };
  const days = Math.round((startOfDay(now) - startOfDay(date)) / 86_400_000);
  if (days === 0) return { iso, label: todayLabel };
  if (days === 1) return { iso, label: yesterdayLabel };
  return { iso, label: formatDate(date, { locale, day: 'numeric', month: 'short' }) };
}

/** Build a CSV string from a list of leads — US-150. */
export function leadsToCsv(
  leads: ApiLead[],
  accounts: AccountLite[],
  businesses: BusinessLite[],
): string {
  const accountsById = new Map(accounts.map((a) => [a.id, a.name]));
  const businessesById = new Map(businesses.map((b) => [b.id, b.name]));
  const headers = [
    'name',
    'company',
    'title',
    'account',
    'business',
    'last_action',
    'last_activity_at',
    'lead_status',
    'validated_from',
  ];
  const lines: string[] = [headers.join(',')];
  for (const lead of leads) {
    const cells = [
      lead.prospectName,
      lead.prospectCompany ?? '',
      lead.prospectTitle ?? '',
      (lead.linkedinAccountId && accountsById.get(lead.linkedinAccountId)) ?? '',
      (lead.businessId && businessesById.get(lead.businessId)) ?? '',
      lead.lastAction ?? '',
      lead.lastActivityAt ? new Date(lead.lastActivityAt).toISOString() : '',
      lead.status,
      lead.validatedFrom,
    ].map(escapeCsv);
    lines.push(cells.join(','));
  }
  return `${lines.join('\n')}\n`;
}

function escapeCsv(cell: string): string {
  // Neutralize CSV formula-injection: a cell starting with = + - @ TAB CR can be
  // executed as a formula by Excel/Sheets. Prospect name/company come from
  // LinkedIn (user-controlled), so prefix dangerous cells with a single quote.
  // (Mirrors the server-side csvEscapeCell — audit M20.)
  const raw = /^[=+\-@\t\r]/.test(cell) ? `'${cell}` : cell;
  // Always quote — keeps the round-trip simple and avoids edge cases with
  // `,`, `"`, `\n` and leading whitespace.
  const escaped = raw.replace(/"/g, '""');
  return `"${escaped}"`;
}

export function csvFilename(now: Date = new Date()): string {
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  return `barel-leads-${yyyy}-${mm}-${dd}.csv`;
}
